'use client'

import {useCallback, useSyncExternalStore} from 'react'

/** Side by side is readable up to three; past that it's a spreadsheet. */
export const COMPARE_MAX = 3

export interface CompareItem {
  slug: string
  name: string
}

const KEY = 'uniap.compare'
const EVENT = 'uniap:compare'
const EMPTY: CompareItem[] = []

/**
 * The universities picked for comparison while browsing.
 *
 * Per browser, in localStorage: it is a scratch pad between search pages,
 * not a list worth an account round trip (the shortlist is that). Every
 * toggle and the bar read the same store, kept in step within the tab by an
 * event and across tabs by `storage`.
 */
let cache: {raw: string | null; items: CompareItem[]} = {raw: null, items: EMPTY}

function read(): CompareItem[] {
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(KEY)
  } catch {
    return EMPTY
  }
  if (raw === cache.raw) return cache.items
  let items: CompareItem[] = EMPTY
  try {
    const parsed = raw ? JSON.parse(raw) : []
    if (Array.isArray(parsed)) {
      items = parsed
        .filter((item) => typeof item?.slug === 'string' && typeof item?.name === 'string')
        .slice(0, COMPARE_MAX)
    }
  } catch {
    items = EMPTY
  }
  cache = {raw, items}
  return items
}

function write(items: CompareItem[]) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items))
  } catch {
    // Private mode or storage blocked: the pick simply isn't remembered.
  }
  window.dispatchEvent(new Event(EVENT))
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

export function useCompareList() {
  // The server has no list; the first client render agrees with it, then
  // the real list arrives.
  const items = useSyncExternalStore(subscribe, read, () => EMPTY)

  const toggle = useCallback((item: CompareItem) => {
    const current = read()
    if (current.some((existing) => existing.slug === item.slug)) {
      write(current.filter((existing) => existing.slug !== item.slug))
    } else if (current.length < COMPARE_MAX) {
      write([...current, item])
    }
  }, [])

  const clear = useCallback(() => write([]), [])

  return {items, toggle, clear}
}

/** Where a set of picks opens. */
export function compareHref(locale: string, items: CompareItem[]): string {
  const query = new URLSearchParams(items.map((item) => ['u', item.slug]))
  return `/${locale}/compare?${query}`
}
